import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { isEmpty, find, isDate } from 'lodash';
import Drawer from '@material-ui/core/Drawer';
import moment from 'moment';
import lodash from 'lodash';

import { addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { toQuery, toMutation, nodeWithArgs, node } from 'common/util/queryHelper';
import { Button, BUTTON } from 'components/common/Button/Button';
import AddEmailLayout from '../components/AddEmailLayout';
import VerifyEmail from '../components/VerifyEmail';
import ReportReminderForm, { FREQUENCY_OPTIONS } from '../components/ReportReminderForm';
import { fetchEmailConfigs } from '../actions/reportActions';
import styles from './AddEmailDrawer.scss';

const ADD_EMAIL_TITLE = 'Add Email';
const EMAIL_CONFIG_FIELDS = [
    'id',
    'institutionId',
    'reportType',
    'groups',
    'selectStart',
    'frequency',
    'isInActiveUser',
    node('user', ['id', 'sub', 'emailAddress', 'displayName', 'firstName', 'lastName']),
];

export const getAddEmailConfigNode = (reportType, emailConfig, institutionId) => {
    return nodeWithArgs(
        'saveUserReportEmailConfiguration',
        { institutionId, input: { reportType, ...emailConfig } },
        EMAIL_CONFIG_FIELDS,
    );
};

export const getEmailConfigByEmailNode = (reportType, email, institutionId) => {
    return nodeWithArgs('getEmailConfigurationByEmail', { reportType, email, institutionId }, EMAIL_CONFIG_FIELDS);
};

const isValidEmailFormat = email => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

const isExistedEmailConfig = emailConfig => {
    return emailConfig && emailConfig.id !== 'NEW';
};

export const AddEmailDrawer = ({
    reportType,
    open,
    onClose,
    selectedEmailConfig,
    addApplicationMessages,
    fetchEmailConfigs,
    institutionId,
}) => {
    const [isLoading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [emailErrorMsg, setEmailErrorMsg] = useState('');
    const [emailConfig, setEmailConfig] = useState(selectedEmailConfig);
    const [isOpenEmailConfigForm, setOpenEmailConfigForm] = useState(isExistedEmailConfig(selectedEmailConfig));
    const [startDate, setStartDate] = useState(isEmpty(emailConfig) ? undefined : new Date(emailConfig.selectStart));
    const [frequency, setFrequency] = useState(
        isEmpty(emailConfig) ? undefined : find(FREQUENCY_OPTIONS, { value: emailConfig.frequency }),
    );
    const [startDateErrorMsg, setStartDateErrorMsg] = useState('');
    const [frequencyErrorMsg, setFrequencyErrorMsg] = useState('');

    useEffect(() => {
        setEmailErrorMsg('');
    }, [email]);

    useEffect(() => {
        setStartDateErrorMsg('');
    }, [startDate]);

    useEffect(() => {
        setFrequencyErrorMsg('');
    }, [frequency]);

    const toggleDrawer = event => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }

        onClose(false);
    };

    const emailChangeHandler = event => {
        setEmail(event.target.value);
    };

    const closeHandler = () => {
        setOpenEmailConfigForm(false);
        onClose(false);
    };

    const verifyEmail = async () => {
        setEmailErrorMsg('');

        if (isValidEmailFormat(email)) {
            setLoading(true);
            try {
                const queryNode = getEmailConfigByEmailNode(reportType, email, institutionId);
                const response = await queryGraphQL(toQuery(queryNode));
                const { getEmailConfigurationByEmail } = response.data;

                if (!isEmpty(getEmailConfigurationByEmail)) {
                    if (getEmailConfigurationByEmail.isInActiveUser) {
                        setEmailErrorMsg(
                            'This email is no longer active and cannot be added to receive reports.',
                        );
                    } else if (getEmailConfigurationByEmail.id !== 'NEW') {
                        setEmailErrorMsg(
                            'This email already exists in the email list available for reports. You may not add the same email twice.',
                        );
                    } else {
                        setEmailConfig(getEmailConfigurationByEmail);
                        setOpenEmailConfigForm(true);
                    }
                } else {
                    setEmailErrorMsg(
                        'This email has not been registered to any users in an institution and cannot be added to receive reports.',
                    );
                }
            } catch (error) {
                addApplicationMessages('error', [error.responseText]);
            }

            setLoading(false);
        } else {
            setEmailErrorMsg('Invalid email format.');
        }
    };

    const nextClickHandler = () => verifyEmail();

    const addOrUpdateEmailClickHandler = async () => {
        if (!isDate(startDate) || isEmpty(frequency)) {
            !isDate(startDate) && setStartDateErrorMsg('Select Start is required.');
            isEmpty(frequency) && setFrequencyErrorMsg('Frequency is required.');

            return;
        }

        const date = moment.utc(moment(startDate)).format();
        const config = {
            reportType,
            institutionId: emailConfig.institutionId,
            userId: emailConfig.user.id,
            frequency: frequency.value,
            selectStart: date,
        };

        setLoading(true);
        const queryNode = getAddEmailConfigNode(reportType, config, institutionId);
        try {
            const response = await mutateGraphQL(toMutation(queryNode));

            if (response.errors) {
                addApplicationMessages(
                    'error',
                    response.errors.map(e => e.message),
                );
                setLoading(false);
            } else {
                closeHandler();
                fetchEmailConfigs(reportType, emailConfig.institutionId);
            }
        } catch (error) {
            addApplicationMessages('error', [error.responseText]);
            setLoading(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={toggleDrawer}
            disableEscapeKeyDown
            classes={{
                root: styles.addEmailDrawer,
            }}
            data-cy="AddEmailDrawer"
        >
            {isOpenEmailConfigForm ? (
                <AddEmailLayout
                    title={isExistedEmailConfig(emailConfig) ? emailConfig.user.displayName : ADD_EMAIL_TITLE}
                    onClose={closeHandler}
                    isLoading={isLoading}
                    bodyContent={
                        <ReportReminderForm
                            emailConfig={emailConfig}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            startDateErrorMsg={startDateErrorMsg}
                            frequency={frequency}
                            setFrequency={setFrequency}
                            frequencyErrorMsg={frequencyErrorMsg}
                        />
                    }
                    actionButton={
                        <Button
                            bgcolor={BUTTON.BGCOLOR.ORANGE}
                            color={BUTTON.COLOR.WHITE}
                            onClick={addOrUpdateEmailClickHandler}
                            data-id="submit-email-config-button"
                            data-cy="AddEmailDrawer-updateBtn"
                        >
                            {isExistedEmailConfig(emailConfig) ? 'Save Changes' : 'Add Email'}
                        </Button>
                    }
                />
            ) : (

                <AddEmailLayout
                    title={ADD_EMAIL_TITLE}
                    onClose={closeHandler}
                    isLoading={isLoading}
                    bodyContent={
                        <VerifyEmail
                            email={email}
                            onEmailChange={emailChangeHandler}
                            emailErrorMsg={emailErrorMsg}
                        />
                    }
                    actionButton={
                        <Button
                            bgcolor={BUTTON.BGCOLOR.ORANGE}
                            color={BUTTON.COLOR.WHITE}
                            onClick={nextClickHandler}
                            disabled={!email}
                            data-id="next-email-config-button"
                            data-cy="AddEmailDrawer-Next"
                        >
                            Next
                        </Button>
                    }
                />
            )}
        </Drawer>
    );
};

export default connect((state, props) => {
    const institutionId = lodash.get(state, 'me.institution.id');

    return {
        institutionId,
    };
}, {
    addApplicationMessages,
    fetchEmailConfigs,
})(AddEmailDrawer);
